import { BOARD_NODES } from '../game/board';

export interface Point { x: number; y: number }
// Calibrated to the lacquer board's side rails, with a wider near edge.
export const BOARD_CORNERS = { farLeft: {x:177,y:113}, farRight:{x:623,y:113}, nearLeft:{x:124,y:365}, nearRight:{x:676,y:365} };
const nearRatio = 223 / 276;
const perspective = nearRatio - 1;

/** A projective plane: all straight routes remain straight. */
export function projectBoardPoint(x: number, y: number): Point {
  const u = (x - 300) / 220, v = (y - 80) / 440, w = 1 + perspective * v;
  return { x: 400 + 223 * u / w, y: (113 + (365 * nearRatio - 113) * v) / w };
}

export function createBoardProjection(compact = false) {
  const project = compact ? (x:number,y:number) => ({x,y}) : projectBoardPoint;
  const source = (id:string) => BOARD_NODES.find(node => node.id === id) ?? BOARD_NODES.find(node=>node.id==='O20')!;
  const at = (id:string, dx=0, dy=0) => { const n=source(id); return project(n.x+dx,n.y+dy); };
  const polygon = (id:string, offsets:Point[]) => offsets.map((p,i)=>{const q=at(id,p.x,p.y);return `${i?'L':'M'}${q.x.toFixed(3)},${q.y.toFixed(3)}`;}).join(' ')+' Z';
  const circle = (id:string, radius:number, dx=0,dy=0) => polygon(id,Array.from({length:48},(_,i)=>({x:dx+Math.cos(i*Math.PI/24)*radius,y:dy+Math.sin(i*Math.PI/24)*radius})));
  const line = (path:string[]) => path.map((id,i)=>{const p=at(id);return `${i?'L':'M'}${p.x},${p.y}`;}).join(' ');
  // Tangent frame for engravings and glyphs. Large rings use exact projected paths above.
  const frame = (id:string) => { const p=at(id),x=at(id,.01,0),y=at(id,0,.01);return `matrix(${(x.x-p.x)*100},${(x.y-p.y)*100},${(y.x-p.x)*100},${(y.y-p.y)*100},${p.x},${p.y})`; };
  const scale = (id:string) => {const p=at(id), q=at(id,1,0);return Math.hypot(q.x-p.x,q.y-p.y);};
  return {compact,project,at,polygon,circle,line,frame,scale};
}
export type BoardProjection = ReturnType<typeof createBoardProjection>;
export const DISPLAY_NODES = BOARD_NODES.map(node => ({ ...node, ...projectBoardPoint(node.x, node.y) }));
